import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Award,
  ClipboardCheck,
  RefreshCw,
  UserPlus,
  Users,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import adminApi from '../../services/adminApi';

import CockpitHome from './CockpitHome';
import {
  AdaptiveSubnav,
  CockpitPanel,
  CockpitTabs,
  CockpitWorkspace,
  CockpitWorkspaceContent,
} from './cockpit';
import { adminRoutes } from './navigation/adminRoutes';
import {
  getRememberedCockpitPath,
  getRememberedSectionPath,
} from './navigation/adminPlaceMemory';
import { canAccessSection, getAdminUser } from './navigation/sectionAccess';

const EMPTY_METRICS = {
  students: null,
  customers: null,
  registrationStats: null,
  studentReviewStats: null,
  historyStats: null,
  belts: null,
};

const METRIC_REQUESTS = [
  ['students', '/api/admin/students'],
  ['customers', '/api/admin/customers'],
  ['registrationStats', '/api/admin/member-registrations/stats'],
  ['studentReviewStats', '/api/admin/student-add-submissions/stats'],
  ['belts', '/api/admin/belts'],
];

const getJson = async (endpoint) => {
  const response = await adminApi.get(endpoint);
  if (!response.ok) {
    throw new Error(`Request failed: ${endpoint}`);
  }
  return response.json();
};

export const countPromotionReadyStudents = (students = [], belts = []) => {
  const activeBelts = belts
    .filter((belt) => belt.is_active)
    .sort((left, right) => left.order - right.order);
  const beltIndexes = new Map(activeBelts.map((belt, index) => [belt.slug, index]));

  return students.filter((student) => {
    if (!student.is_active) return false;

    const beltIndex = beltIndexes.get(student.current_belt);
    if (beltIndex === undefined || beltIndex >= activeBelts.length - 1) return false;

    const maxStripes = Number(activeBelts[beltIndex].max_stripes);
    return maxStripes > 0 && Number(student.current_stripes || 0) >= maxStripes;
  }).length;
};

const metricValue = (value) => (value === null || value === undefined ? '—' : value);

const getSubsectionId = (section, search) => {
  if (!section?.subSections?.length) return null;
  const requestedView = new URLSearchParams(search).get('view');
  const subsectionIds = section.subSections.map((subsection) => subsection.id);
  if (subsectionIds.includes(requestedView)) return requestedView;
  return section.subSections[0].id;
};

export const StudentsCockpitWorkspace = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const adminUser = useMemo(() => getAdminUser(), []);
  const hasAccess = useCallback(
    (sectionId) => canAccessSection(sectionId, adminUser),
    [adminUser]
  );
  const canReviewHistory = hasAccess('historyreviews');
  const sections = useMemo(
    () => adminRoutes.students.sections.filter((section) => hasAccess(section.id)),
    [hasAccess]
  );
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const activeSection = sections.find((section) => section.path === normalizedPath);
  const activeSubsectionId = getSubsectionId(activeSection, location.search);
  const activeSubsection = activeSection?.subSections?.find(
    (subsection) => subsection.id === activeSubsectionId
  );

  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const [loading, setLoading] = useState(true);
  const [failedMetrics, setFailedMetrics] = useState([]);

  const loadMetrics = useCallback(async () => {
    setLoading(true);

    const requests = canReviewHistory
      ? [...METRIC_REQUESTS, ['historyStats', '/api/admin/history-submissions/stats']]
      : METRIC_REQUESTS;
    const results = await Promise.allSettled(
      requests.map(([, endpoint]) => getJson(endpoint))
    );

    const nextMetrics = { ...EMPTY_METRICS };
    const failures = [];
    results.forEach((result, index) => {
      const [key] = requests[index];
      if (result.status === 'fulfilled') {
        nextMetrics[key] = result.value;
      } else {
        failures.push(key);
      }
    });

    setMetrics(nextMetrics);
    setFailedMetrics(failures);
    setLoading(false);
  }, [canReviewHistory]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const activeStudents = Array.isArray(metrics.students)
    ? metrics.students.filter((student) => student.is_active).length
    : null;
  const memberAccounts = Array.isArray(metrics.customers) ? metrics.customers.length : null;
  const pendingRegistrations = metrics.registrationStats
    ? (metrics.registrationStats.pending_verification || 0)
      + (metrics.registrationStats.pending_approval || 0)
    : null;
  const readyRegistrations = metrics.registrationStats?.pending_approval;
  const pendingStudentReviews = metrics.studentReviewStats?.pending;
  const pendingHistoryReviews = metrics.historyStats?.pending;
  const promotionReady = Array.isArray(metrics.students) && Array.isArray(metrics.belts)
    ? countPromotionReadyStudents(metrics.students, metrics.belts)
    : null;

  const kpis = useMemo(() => {
    const items = [
      {
        id: 'active-students',
        title: 'Active students',
        value: metricValue(activeStudents),
        subtitle: memberAccounts === null ? 'Member count unavailable' : `${memberAccounts} member accounts`,
        icon: Users,
      },
      {
        id: 'pending-registrations',
        title: 'Pending registrations',
        value: metricValue(pendingRegistrations),
        subtitle: readyRegistrations === null || readyRegistrations === undefined
          ? 'Approval count unavailable'
          : `${readyRegistrations} ready for approval`,
        icon: UserPlus,
        tone: pendingRegistrations > 0 ? 'warn' : undefined,
      },
      {
        id: 'student-reviews',
        title: 'Add student reviews',
        value: metricValue(pendingStudentReviews),
        subtitle: 'Parent-submitted requests',
        icon: ClipboardCheck,
        tone: pendingStudentReviews > 0 ? 'warn' : undefined,
      },
      {
        id: 'promotion-ready',
        title: 'Promotion ready',
        value: metricValue(promotionReady),
        subtitle: 'At configured max stripes',
        icon: Award,
        tone: promotionReady > 0 ? 'good' : undefined,
      },
    ];

    if (canReviewHistory) {
      items.splice(3, 0, {
        id: 'history-reviews',
        title: 'History reviews',
        value: metricValue(pendingHistoryReviews),
        subtitle: 'Awaiting instructor review',
        icon: ClipboardCheck,
        tone: pendingHistoryReviews > 0 ? 'warn' : undefined,
      });
    }

    return items;
  }, [
    activeStudents,
    canReviewHistory,
    memberAccounts,
    pendingHistoryReviews,
    pendingRegistrations,
    pendingStudentReviews,
    promotionReady,
    readyRegistrations,
  ]);

  const quickActions = [
    hasAccess('registrations') && {
      id: 'approve-registration',
      label: readyRegistrations > 0
        ? `Approve registrations (${readyRegistrations})`
        : 'Approve registrations',
      icon: ClipboardCheck,
      path: '/admin/students/registrations',
    },
    hasAccess('students') && {
      id: 'add-student',
      label: 'Add student',
      icon: UserPlus,
      path: '/admin/students/list?action=add',
    },
    hasAccess('belts') && {
      id: 'belt-config',
      label: 'Open belt config',
      icon: Award,
      path: '/admin/students/belts',
    },
  ].filter(Boolean);

  if (!activeSection) {
    return sections[0]
      ? <Navigate to={getRememberedCockpitPath('students', sections, adminUser)} replace />
      : <Navigate to="/admin" replace />;
  }

  const tabPanelId = `students-cockpit-tab-panel-${activeSection.id}`;

  return (
    <CockpitHome
      categoryId="students"
      description="Student records, memberships, registrations, and progression at a glance"
      kpis={kpis}
      quickActions={quickActions}
      canAccessSection={hasAccess}
      showSectionLinks={false}
      alerts={failedMetrics.length > 0 && (
        <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2 text-sm text-amber-900 dark:text-amber-200">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Some dashboard metrics could not be loaded. Available data is still shown.</span>
            </div>
            <Button variant="outline" size="sm" onClick={loadMetrics} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
    >
      <CockpitTabs
        tabs={sections}
        active={activeSection.id}
        onChange={(sectionId) => {
          const section = sections.find((candidate) => candidate.id === sectionId);
          if (section) navigate(getRememberedSectionPath(section, adminUser));
        }}
        label="Students and membership sections"
        idPrefix="students-cockpit-tab"
      />

      <CockpitPanel
        id={tabPanelId}
        aria-labelledby={`students-cockpit-tab-${activeSection.id}`}
      >
        {activeSection.subSections?.length ? (
          <CockpitWorkspace>
            <AdaptiveSubnav
              tabs={activeSection.subSections}
              active={activeSubsectionId}
              onChange={(subsectionId) => {
                const subsection = activeSection.subSections.find(
                  (candidate) => candidate.id === subsectionId
                );
                if (subsection) navigate(subsection.path);
              }}
              label={`${activeSection.label} views`}
              idPrefix={`students-${activeSection.id}-view`}
            />
            <CockpitWorkspaceContent
              role="tabpanel"
              id={`students-${activeSection.id}-view-panel-${activeSubsectionId}`}
              aria-labelledby={`students-${activeSection.id}-view-${activeSubsectionId}`}
            >
              <Outlet context={{ activeSection, activeSubsection }} />
            </CockpitWorkspaceContent>
          </CockpitWorkspace>
        ) : (
          <CockpitWorkspaceContent>
            <Outlet context={{ activeSection, activeSubsection: null }} />
          </CockpitWorkspaceContent>
        )}
      </CockpitPanel>
    </CockpitHome>
  );
};

export default StudentsCockpitWorkspace;
